import { useEffect, useState, type JSX } from 'react';
import { Icon, type IconName } from '../lib/icons.tsx';
import { displayHost, type ProjectRow } from '../lib/project.ts';
import { TabBar } from './TabBar.tsx';

export type SessionTab = 'home' | 'chat' | 'task' | 'git' | 'settings' | 'config' | 'projects';

const TABS: { id: SessionTab; label: string; icon: IconName }[] = [
  { id: 'home', label: 'Home', icon: 'home' },
  { id: 'chat', label: 'Chat', icon: 'chat' },
  { id: 'task', label: 'Task', icon: 'task' },
  { id: 'git', label: 'Git', icon: 'git' },
  { id: 'settings', label: 'Settings', icon: 'settings' },
  { id: 'projects', label: 'Projects', icon: 'projects' },
  { id: 'config', label: 'More', icon: 'more' },
];

const SESSION_ONLY = new Set<SessionTab>(['chat', 'task', 'settings']);

function finishedLabel(count: number, suffix = ''): { count: number; label: string } {
  return {
    count,
    label: `${count} finished task${count === 1 ? '' : 's'}${suffix}`,
  };
}

export function SessionTabs({
  tab,
  gitDirty,
  doneTasks = 0,
  doneTasksOther = 0,
  inSession = true,
  projects,
  selectedProjectId,
  doneByProject,
  onSelectProject,
  onTab,
}: {
  tab: SessionTab | null;
  gitDirty?: boolean;
  /** Tasks in this project that have finished, badged on Home. */
  doneTasks?: number;
  /** Finished tasks in other projects, badged on Projects. */
  doneTasksOther?: number;
  /** Chat, Task, and Settings need an open chat. */
  inSession?: boolean;
  projects: readonly ProjectRow[];
  selectedProjectId: string | null;
  doneByProject?: Readonly<Record<string, number>>;
  onSelectProject: (id: string) => void;
  onTab: (tab: Exclude<SessionTab, 'projects'>) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(false);
  }, [tab]);

  useEffect(() => {
    if (!open) return;
    const close = (e: Event): void => {
      const cell = document.getElementById('tab-projects')?.closest('.session-tab-cell');
      if (e.target instanceof Node && cell?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    addEventListener('pointerdown', close);
    addEventListener('keydown', onKey);
    addEventListener('scroll', close, { passive: true });
    return () => {
      removeEventListener('pointerdown', close);
      removeEventListener('keydown', onKey);
      removeEventListener('scroll', close);
    };
  }, [open]);

  return (
    <TabBar
      label="Session"
      tab={open ? 'projects' : tab}
      items={TABS.map((item) => ({
        ...item,
        icon: <Icon name={item.icon} />,
        disabled: !inSession && SESSION_ONLY.has(item.id),
        popup: item.id === 'projects',
      }))}
      onTab={(next) => {
        if (next === 'projects') {
          setOpen((v) => !v);
          return;
        }
        setOpen(false);
        onTab(next);
      }}
      mark={(id) => (id === 'git' && gitDirty ? 'dirty' : null)}
      badge={(id) =>
        id === 'home' && doneTasks > 0
          ? finishedLabel(doneTasks)
          : id === 'projects' && doneTasksOther > 0 && !open
            ? finishedLabel(doneTasksOther, ' in other projects')
            : null
      }
      addon={(id) =>
        id === 'projects' && open ? (
          <div className="project-picker">
            <div
              className="btn-group btn-group-col project-picker-menu"
              role="radiogroup"
              aria-label="Project"
            >
              {projects.length === 0 && (
                <span className="btn-group-item project-picker-empty">No projects configured</span>
              )}
              {projects.map((p) => {
                const on = p.id === selectedProjectId;
                const done = doneByProject?.[p.id] ?? 0;
                return (
                  <button
                    key={p.id}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    className={`btn-group-item${on ? ' btn-group-item-on' : ''}`}
                    onClick={() => {
                      setOpen(false);
                      onSelectProject(p.id);
                    }}
                  >
                    <span className="project-picker-name">{p.name}</span>
                    {p.desktop && (
                      <span className="project-picker-host">
                        {displayHost(p.activeHost ?? (p.desktopOnline ? p.desktop.host : 'server'))}
                      </span>
                    )}
                    {done > 0 && (
                      <span
                        className="project-row-badge"
                        aria-label={`${done} finished task${done === 1 ? '' : 's'}`}
                      >
                        {done > 99 ? '99+' : done}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null
      }
    />
  );
}
